import React, { Fragment, Suspense, useState } from 'react';

import { useIntl } from 'react-intl-next';

import { Inline } from '@atlaskit/primitives/compiled';

import type { FeedbackSubmitHandlerProps } from '../../../ai-answer-dialog/types';

import { AIFeedbackButtons } from './ai-feedback-buttons';
import {
	FeedbackCollectorModal,
	FeedbackCollectorModalVariant,
} from './ai-feedback-collector-modal';
import { AsyncAIFeedbackFlag } from './ai-feedback-flags-async';
import { messages } from './messages';

type AIFeedbackProps = {
	entrypointId: string;
	userDetails: {
		name?: string;
		id?: string;
	};
	attributes: Record<string, string | null | undefined>;
	attributesWithConsent?: Record<string, string>;
	onLikeButtonClickHandler?: (e?: any) => void;
	onDislikeButtonClickHandler?: (e?: any) => void;
	onDeselectLikeButtonClickHandler?: (e?: any) => void;
	onDeselectDislikeButtonClickHandler?: (e?: any) => void;
	onFeedbackSubmitHandler?: ({
		liked,
		feedbackOptions,
		additionalFeedbackAdded,
		includeResponse,
		canContact,
	}: FeedbackSubmitHandlerProps) => void;
	liked?: boolean;
};

export const AIFeedback = function AIFeedback({
	entrypointId,
	onLikeButtonClickHandler,
	onDislikeButtonClickHandler,
	onDeselectLikeButtonClickHandler,
	onDeselectDislikeButtonClickHandler,
	onFeedbackSubmitHandler,
	userDetails,
	attributes,
	attributesWithConsent = {},
	liked: externalLike, // set like state from the outside
}: AIFeedbackProps): React.JSX.Element {
	const { formatMessage } = useIntl();

	const [showFeedbackModal, setShowFeedbackModal] = useState(false);
	const [showDisplayFlag, setShowDisplayFlag] = useState(false);
	const [liked, setLiked] = useState<boolean | undefined>(externalLike);

	const onFeedbackSubmit = ({
		feedbackOptions,
		additionalFeedbackAdded,
		includeResponse,
		canContact,
	}: Omit<FeedbackSubmitHandlerProps, 'liked'>) => {
		onFeedbackSubmitHandler?.({
			liked,
			feedbackOptions,
			additionalFeedbackAdded,
			includeResponse,
			canContact,
		});
	};
	return (
		<>
			<Inline alignBlock="center" space="space.050">
				<AIFeedbackButtons
					setShowDisplayFlag={setShowDisplayFlag}
					onLikeButtonClickHandler={onLikeButtonClickHandler}
					onDislikeButtonClickHandler={onDislikeButtonClickHandler}
					onDeselectLikeButtonClickHandler={onDeselectLikeButtonClickHandler}
					onDeselectDislikeButtonClickHandler={onDeselectDislikeButtonClickHandler}
					liked={liked}
					setLiked={setLiked}
				/>
			</Inline>
			{showFeedbackModal ? (
				<FeedbackCollectorModal
					entrypointId={entrypointId}
					attributes={attributes}
					attributesWithConsent={attributesWithConsent}
					userId={userDetails.id}
					userName={userDetails.name}
					onClose={() => setShowFeedbackModal(false)}
					onSubmit={onFeedbackSubmit}
					variant={
						liked ? FeedbackCollectorModalVariant.POSITIVE : FeedbackCollectorModalVariant.NEGATIVE
					}
				/>
			) : null}
			<Suspense fallback={<Fragment />}>
				<AsyncAIFeedbackFlag
					displayFlag={showDisplayFlag}
					onDismissed={() => setShowDisplayFlag(false)}
					description={formatMessage(messages.ai_feedback_flag_description)}
					title={formatMessage(messages.ai_feedback_flag_title)}
					actions={[
						{
							content: formatMessage(messages.ai_feedback_provide_additional_feedback_title),
							onClick: () => {
								setShowDisplayFlag(false);
								setShowFeedbackModal(true);
							},
						},
						{
							content: formatMessage(messages.ai_feedback_flag_close_action),
							onClick: () => setShowDisplayFlag(false),
						},
					]}
				/>
			</Suspense>
		</>
	);
};
